import { useState } from "react";
import { parseAirtableCSV, importCasesBatch, TransformedCase } from "@/lib/airtableImport";
import { formatParisDate } from "@/lib/parisDateTime";
import { supabase } from "@/lib/supabaseClient";

export default function ImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [parsedData, setParsedData] = useState<TransformedCase[]>([]);
  const [importResults, setImportResults] = useState<any>(null);
  const [migrationResults, setMigrationResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv")) {
      setError("Le fichier doit être un CSV");
      return;
    }

    setFile(selectedFile);
    setError(null);
    setParsedData([]);
    setImportResults(null);
  };

  const handleParse = async () => {
    if (!file) return;

    setParsing(true);
    setError(null);

    try {
      const parsed = await parseAirtableCSV(file);
      setParsedData(parsed);
      console.log(`✅ ${parsed.length} lignes parsées avec succès`);
    } catch (err) {
      setError(`Erreur de parsing: ${err}`);
      console.error(err);
    } finally {
      setParsing(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm("⚠️ ATTENTION : Cela va supprimer TOUS les dossiers et toutes les données associées (messages, devis, factures, etc.). Cette action est IRRÉVERSIBLE.\n\nÊtes-vous absolument sûr de vouloir continuer ?")) {
      return;
    }

    if (!confirm("⚠️ DERNIÈRE CONFIRMATION : Tous les dossiers seront supprimés définitivement. Confirmer ?")) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      // Supprimer tous les cases (les suppressions en cascade géreront les relations)
      const { error: deleteError } = await supabase
        .from("cases")
        .delete()
        .neq("id", "00000000-0000-0000-0000-000000000000"); // Condition qui matche tous les IDs

      if (deleteError) {
        throw new Error(deleteError.message);
      }

      alert("✅ Tous les dossiers ont été supprimés avec succès.");
      console.log("✅ Suppression complète effectuée");
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      setError(`Erreur lors de la suppression : ${errorMsg}`);
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleMigrateQuotes = async () => {
    if (!confirm("🚀 Migrer les devis Airtable vers Supabase Storage ?\n\nCette opération se fera par batches de 10 dossiers pour éviter les timeouts.\n\nVous verrez la progression en temps réel.")) {
      return;
    }

    setMigrating(true);
    setError(null);
    setMigrationResults(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error("Utilisateur non authentifié");
      }

      const API_URL =
        typeof window !== "undefined" && window.location.origin.includes("localhost")
          ? "http://localhost:3000"
          : window.location.origin;

      let offset = 0;
      const limit = 10; // Batch size
      let totalDownloaded = 0;
      let totalFailed = 0;
      let totalProcessed = 0;
      let batchNumber = 1;

      // Boucle sur les batches
      while (true) {
        console.log(`\n🚀 Batch ${batchNumber}: offset=${offset}, limit=${limit}`);

        const response = await fetch(`${API_URL}/api/airtable/migrate-quotes`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ offset, limit }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }

        const results = await response.json();
        console.log(`✅ Batch ${batchNumber} terminé:`, results);

        totalDownloaded += results.totalQuotesDownloaded || 0;
        totalFailed += results.totalQuotesFailed || 0;
        totalProcessed += results.processed || 0;

        // Mettre à jour les résultats affichés
        setMigrationResults({
          totalCases: results.totalCases,
          processed: totalProcessed,
          remaining: results.remaining,
          totalQuotesDownloaded: totalDownloaded,
          totalQuotesFailed: totalFailed,
          batchNumber,
        });

        // Si plus rien à traiter, on arrête
        if (results.remaining === 0 || results.processed === 0) {
          console.log("✨ Migration complète terminée!");
          break;
        }

        // Passer au batch suivant
        offset += limit;
        batchNumber++;

        // Petite pause pour éviter de surcharger l'API
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      alert(`✅ Migration terminée !\n\n${totalDownloaded} devis migrés avec succès\n${totalFailed} échecs\n${totalProcessed} dossiers traités`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      setError(`Erreur de migration : ${errorMsg}`);
      console.error(err);
    } finally {
      setMigrating(false);
    }
  };

  const handleImport = async () => {
    if (parsedData.length === 0) return;

    if (!confirm(`Êtes-vous sûr de vouloir importer ${parsedData.length} dossiers dans Supabase ?\n\n⚠️ Les devis ne seront PAS téléchargés automatiquement.\nUtilisez le bouton "Migrer les devis Airtable" après l'import.`)) {
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const results = await importCasesBatch(parsedData, 500);
      setImportResults(results);
      console.log("Résultats d'import:", results);
    } catch (err) {
      setError(`Erreur d'import: ${err}`);
      console.error(err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Import CSV Airtable
          </h1>
          
          <div className="flex gap-3">
            <button
              onClick={handleMigrateQuotes}
              disabled={migrating || deleting || importing || parsing}
              className="px-4 py-2 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {migrating ? "📥 Migration en cours..." : "📥 Migrer les devis Airtable"}
            </button>
            
            <button
              onClick={handleDeleteAll}
              disabled={deleting || importing || parsing || migrating}
              className="px-4 py-2 bg-red-600 text-white font-medium text-sm rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {deleting ? "🗑️ Suppression..." : "🗑️ Supprimer tous les dossiers"}
            </button>
          </div>
        </div>

        {/* Upload */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">1. Sélectionner le fichier CSV</h2>
          
          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
          />

          {file && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                📄 Fichier sélectionné : <strong>{file.name}</strong> ({(file.size / 1024 / 1024).toFixed(2)} Mo)
              </p>
            </div>
          )}

          {file && parsedData.length === 0 && (
            <button
              onClick={handleParse}
              disabled={parsing}
              className="mt-4 px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {parsing ? "⏳ Parsing en cours..." : "2. Parser le CSV"}
            </button>
          )}
        </div>

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-900 font-medium">❌ Erreur</p>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Prévisualisation */}
        {parsedData.length > 0 && !importResults && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Prévisualisation ({parsedData.length} lignes)</h2>

            <div className="overflow-x-auto mb-4">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Société</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">PAX</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Centre</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date événement</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {parsedData.slice(0, 10).map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-900">{row.company_name || "-"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{row.pax || "-"}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{row.center || "-"}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{row.client_email || "-"}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                        {row.event_date
                          ? formatParisDate(row.event_date, {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {parsedData.length > 10 && (
              <p className="text-sm text-gray-500 mb-4">
                ... et {parsedData.length - 10} autres lignes
              </p>
            )}

            <button
              onClick={handleImport}
              disabled={importing}
              className="px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {importing ? "⏳ Import en cours..." : `3. Importer ${parsedData.length} dossiers`}
            </button>
          </div>
        )}

        {/* Résultats d'import */}
        {importResults && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">✅ Import terminé</h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Total dossiers</p>
                <p className="text-2xl font-bold text-gray-900">{importResults.total}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600">Succès</p>
                <p className="text-2xl font-bold text-green-900">{importResults.success}</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-600">Doublons</p>
                <p className="text-2xl font-bold text-yellow-900">{importResults.duplicates || 0}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-600">Erreurs</p>
                <p className="text-2xl font-bold text-red-900">{importResults.errors}</p>
              </div>
            </div>

            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                ℹ️ Les dossiers sont importés. Utilisez le bouton <strong>"Migrer les devis Airtable"</strong> pour télécharger les PDFs.
              </p>
            </div>

            {importResults.errorDetails && importResults.errorDetails.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-red-900 mb-2">Détails des erreurs :</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {importResults.errorDetails.map((err: any, i: number) => (
                    <div key={i} className="p-3 bg-red-50 border border-red-200 rounded text-xs">
                      <p className="font-medium text-red-900">Ligne {err.index + 1}</p>
                      <p className="text-red-700">{err.error}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => {
                setFile(null);
                setParsedData([]);
                setImportResults(null);
                setMigrationResults(null);
              }}
              className="mt-6 px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Importer un autre fichier
            </button>
          </div>
        )}

        {/* Résultats de migration */}
        {migrationResults && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
            <h2 className="text-lg font-semibold mb-4">
              {migrationResults.remaining > 0 ? "📥 Migration en cours..." : "✅ Migration des devis terminée"}
            </h2>

            {/* Progression */}
            {migrationResults.remaining > 0 && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Batch {migrationResults.batchNumber} en cours...</span>
                  <span>{migrationResults.processed}/{migrationResults.totalCases} dossiers traités</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(migrationResults.processed / migrationResults.totalCases) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600">Total dossiers</p>
                <p className="text-2xl font-bold text-blue-900">{migrationResults.totalCases}</p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-lg">
                <p className="text-sm text-indigo-600">Traités</p>
                <p className="text-2xl font-bold text-indigo-900">{migrationResults.processed}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-600">Devis migrés</p>
                <p className="text-2xl font-bold text-green-900">{migrationResults.totalQuotesDownloaded}</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-red-600">Échecs</p>
                <p className="text-2xl font-bold text-red-900">{migrationResults.totalQuotesFailed}</p>
              </div>
            </div>

            {migrationResults.remaining === 0 && migrationResults.totalQuotesFailed > 0 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>{migrationResults.totalQuotesFailed}</strong> devis n'ont pas pu être téléchargés (URLs expirées ou inaccessibles)
                </p>
              </div>
            )}

            {migrationResults.remaining === 0 && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium">
                  ✅ Migration complète terminée ! Consultez les logs Vercel pour plus de détails.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
